// src/services/unleashedContactsLink.ts
// -------------------------------------
// PURE planner for the Link-Contacts GUID backfill (Unleashed Contacts arm, C4).
// Mirrors planCustomerSync's link bucket: match each portal membership (a
// user_shelters row) to its existing Unleashed Customer Contact and record that
// Contact's GUID on user_shelters.unleashed_contact_guid — so auto-CREATE never
// duplicates the already-imported contacts.
//
// The Link utility reads Contacts straight off the Customers GET (Contacts[] returns
// on each customer), so the input is one { customerCode, contacts[] } per customer.
// No Unleashed write happens here — the GUID write is a client-side update under the
// admin's own RLS session (like the Customer link).

import type { SlimContact } from "../../supabase/functions/_shared/unleashed/contactMap";

// A portal membership considered for linking.
export interface LinkMembership {
  id: string;
  shelter_code: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  unleashed_contact_guid: string | null;
}

// One Unleashed customer's code + its contacts (built by the UI from the raw
// Customers GET via contactsFromCustomer).
export interface CustomerContacts {
  customerCode: string;
  contacts: SlimContact[];
}

export interface ContactLinkRow {
  membershipId: string;
  shelterCode: string;
  name: string;
  email: string | null;
  contactGuid: string;
}

// WHY a membership couldn't be linked. Kept distinct because they call for very
// different fixes: a missing customer is a code problem, an empty contact list is
// an Unleashed data problem, and no-match is usually a name/email difference.
export type ContactUnmatchedReason =
  | "no_customer" // that shelter_code isn't in the Unleashed Customers response
  | "customer_has_no_contacts" // customer found, but its Contacts[] is empty
  | "no_contact_match" // contacts present, none matched by email or by first+last
  | "match_without_guid"; // a contact matched but carried no GUID

export interface UnmatchedContactRow {
  membershipId: string;
  shelterCode: string;
  name: string;
  email: string | null;
  reason: ContactUnmatchedReason;
}

export interface ContactLinkPlan {
  toLink: ContactLinkRow[];
  alreadyLinkedCount: number; // membership already carries a Contact GUID
  // Every membership that couldn't be linked, WITH the reason — the count alone
  // told you something was wrong but not what, which made 22 failures out of 641
  // impossible to diagnose without hand-querying the database.
  unmatched: UnmatchedContactRow[];
  unmatchedCount: number; // === unmatched.length (kept for existing callers)
}

const norm = (s: unknown): string =>
  String(s ?? "")
    .trim()
    .toLowerCase();

// Match a membership to an Unleashed contact. Because the import now stores the RAW
// name (suffix intact) — e.g. "Skinner (Crisi Accommodation)" verbatim — both sides
// carry the same string, so a plain exact-name compare works and DISAMBIGUATES
// contacts that share an email (the suffix makes each name unique). No role-stripping.
//
// ── CHANGE LOG (25 Aug 2026) — role-stripping REMOVED, Email+First+Last adopted ──
// Previously the import cleaned names (storing "Skinner" while Unleashed kept the
// suffix), which forced a role-stripped fallback here and made email-only matching
// mis-link people who share an email. With raw names that whole class of problem is
// gone. See docs/contact-import-raw-name-and-role-plan.md.
//
// Order (first hit wins), everything trimmed + lowercased via norm():
//   1. Email + exact First + Last — the precise case; when several contacts share an
//      email, the name picks the right one.
//   2. Exact First + Last alone — covers no-email contacts (they can't match on
//      email) and ones whose Unleashed email is blank/differs; raw names are unique.
//   3. Email alone — last resort, only when OUR side has no name at all.
//
// Residual ambiguity: two contacts in one customer with an IDENTICAL raw name are a
// genuine duplicate — step 2 takes whichever Unleashed returns first. If that ever
// needs flagging instead of guessing, this is the place.
function matchContact(m: LinkMembership, contacts: SlimContact[]): SlimContact | null {
  const email = norm(m.email);
  const fn = norm(m.first_name);
  const ln = norm(m.last_name);
  if (email && (fn || ln)) {
    const hit = contacts.find(
      (c) => norm(c.email) === email && norm(c.firstName) === fn && norm(c.lastName) === ln
    );
    if (hit) return hit;
  }
  if (fn || ln) {
    const byName = contacts.find((c) => norm(c.firstName) === fn && norm(c.lastName) === ln);
    if (byName) return byName;
  }
  if (email && !fn && !ln) {
    const byEmail = contacts.find((c) => norm(c.email) === email);
    if (byEmail) return byEmail;
  }
  return null;
}

export function planContactsLink(
  memberships: LinkMembership[],
  customers: CustomerContacts[]
): ContactLinkPlan {
  const byCode = new Map<string, SlimContact[]>();
  for (const cu of customers) byCode.set(norm(cu.customerCode), cu.contacts || []);

  const toLink: ContactLinkRow[] = [];
  const unmatched: UnmatchedContactRow[] = [];
  let alreadyLinkedCount = 0;

  for (const m of memberships) {
    if (m.unleashed_contact_guid) {
      alreadyLinkedCount += 1;
      continue;
    }
    const name = [m.first_name, m.last_name].filter(Boolean).join(" ").trim();
    const key = norm(m.shelter_code);
    const known = byCode.has(key);
    const contacts = byCode.get(key) || [];
    const match = matchContact(m, contacts);
    if (match?.guid) {
      toLink.push({
        membershipId: m.id,
        shelterCode: m.shelter_code,
        name,
        email: m.email,
        contactGuid: match.guid,
      });
      continue;
    }
    // Narrow the failure as far as the data allows. NB matching is scoped to the
    // membership's own customer, so a person who exists in Unleashed under a
    // DIFFERENT customer still lands here — that's the shared-email case.
    let reason: ContactUnmatchedReason;
    if (!known) reason = "no_customer";
    else if (contacts.length === 0) reason = "customer_has_no_contacts";
    else if (match) reason = "match_without_guid";
    else reason = "no_contact_match";
    unmatched.push({
      membershipId: m.id,
      shelterCode: m.shelter_code,
      name,
      email: m.email,
      reason,
    });
  }

  return { toLink, alreadyLinkedCount, unmatched, unmatchedCount: unmatched.length };
}
